import { IServerUsersPlaceScope } from '../controllers/ServerUsersPlaceCtrl';
import { FeatureFlags } from '../FeatureFlags';
import { IActionContext } from '../helpers/ActionContext';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResult } from '../ServerApi/types/IResponse';
import { IServerUser } from '../ServerApi/types/IServerUser';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { ActionHelpers } from './ActionHelpers';
import { IActionButton } from './ActionPanelService';
import { DeleteUsersFromServerAction } from './DeleteUsersFromServerAction';
import { EditSiteMembershipAction } from './EditSiteMembershipAction';
import { ForcePasswordUpdateAction } from './ForcePasswordUpdateAction';
import { ResetLoginRateLimitingAction } from './ResetLoginRateLimitingAction';
import { ServerService } from './ServerService';

export class ServerUsersActions {

  private serverUsersActionDefinition: IActionButton<IServerUser, {}>[];

  public static $inject = [
    'ServerService',
    'DeleteUsersFromServerAction',
    'ForcePasswordUpdateAction',
    'ResetLoginRateLimitingAction',
    'EditSiteMembershipAction'];

  constructor(
    private ServerService: ServerService,
    private DeleteUsersFromServerAction: DeleteUsersFromServerAction,
    private ForcePasswordUpdateAction: ForcePasswordUpdateAction,
    private ResetLoginRateLimitingAction: ResetLoginRateLimitingAction,
    private EditSiteMembershipAction: EditSiteMembershipAction) {

    this.serverUsersActionDefinition = [
      this.editSiteMembershipAction(),
      this.deleteUsersFromServerAction(),
    ];

    if (FeatureFlags.localAuthPasswordExpirationEnabled && ServerService.isServerDomainFamilyLocal()) {
      this.serverUsersActionDefinition.push(this.forcePasswordUpdateAction());
    }
    if (FeatureFlags.localAuthLoginRateLimitingEnabled && ServerService.isServerDomainFamilyLocal()) {
      this.serverUsersActionDefinition.push(this.resetLoginRateLimitingAction());
    }
  }

  // public API

  public getServerUsersActionsButtons() {
    return this.serverUsersActionDefinition;
  }

  // action button factory

  private editSiteMembershipAction(): IActionButton<IServerUser, {}> {
    return {
      name: 'siteMembership',
      translate: 'siteMembership_action',
      isAvailable: (items: Set<IServerUser>): boolean => this.canEditSiteMembership(items),
      execute: (items: Set<IServerUser>, context: IActionContext) => {
        const scope = <IServerUsersPlaceScope>context.scope;
        this.EditSiteMembershipAction.showEditSiteMembershipDialog(items, context)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectNotFound(scope.selectedItems, result, CommonErrorCode.SystemUserNotFound);
            }
          });
      }
    };
  }

  private canEditSiteMembership(serverUsers: Set<IServerUser>): boolean {
    return this.hasUsersAndNotOnlyGuest(serverUsers);
  }

  private deleteUsersFromServerAction(): IActionButton<IServerUser, {}> {
    return {
      name: 'delete',
      translate: 'delete_action',
      isAvailable: (items: Set<IServerUser>): boolean => this.canDelete(items),
      execute: (items: Set<any>, context: IActionContext) => {
        const scope = <IServerUsersPlaceScope>context.scope;
        this.DeleteUsersFromServerAction.showDeleteUsersFromServerDialog(items, context)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SystemUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private canDelete(serverUsers: Set<IServerUser>): boolean {
    return this.hasUsersAndNotOnlyGuest(serverUsers);
  }

  private forcePasswordUpdateAction(): IActionButton<IServerUser, {}> {
    return {
      name: 'forcePasswordUpdate',
      translate: 'forcePasswordUpdate_action',
      isAvailable: (items: Set<IServerUser>): boolean => this.hasUsersAndNotOnlyGuest(items),
      execute: (items: Set<IServerUser>, context: IActionContext) => {
        const scope = <IServerUsersPlaceScope>context.scope;
        this.ForcePasswordUpdateAction.onServerUsers(context, items)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SystemUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private resetLoginRateLimitingAction(): IActionButton<IServerUser, {}> {
    return {
      name: 'resetLoginRateLimiting',
      translate: 'resetLoginRateLimiting_action',
      isAvailable: (items: Set<IServerUser>): boolean => this.hasUsersAndNotOnlyGuest(items),
      execute: (items: Set<IServerUser>, context: IActionContext) => {
        const scope = <IServerUsersPlaceScope>context.scope;
        this.ResetLoginRateLimitingAction.onServerUsers(context, items)
          .then((result: IResult) => {
            if (result) {
              ActionHelpers.deselectAllExceptErrors(scope.selectedItems, result, CommonErrorCode.SystemUserNotFound);
              scope.refreshContent();
            }
          });
      }
    };
  }

  private hasUsersAndNotOnlyGuest(serverUsers: Set<IServerUser>): boolean {
    return serverUsers.size() > 0 && !ServerUsersActions.isSingleSelectAndGuest(serverUsers);
  }

  private static isSingleSelectAndGuest(serverUsers: Set<IServerUser>) {
    return (serverUsers.size() === 1 && serverUsers.first().maxSiteRole === SiteRoles.Guest);
  }

}

Module.instance().service('ServerUsersActions', ServerUsersActions);
